import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

// Pure PDF.js viewer — renders each page to a data URL and shows via <img>.
// No imperative DOM manipulation, so React can safely mount/unmount at any time.

let _pdfjs = null;
async function loadPdfjs() {
  if (_pdfjs) return _pdfjs;
  const mod = await import(/* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf.js");
  const pdfjsLib = mod.default || mod;
  // Same-origin worker file (public/pdf.worker.min.js). No CDN, no CSP issues.
  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
  _pdfjs = pdfjsLib;
  return pdfjsLib;
}

export function PdfViewer({ arrayBuffer, onError }) {
  const [pages, setPages] = useState([]);       // [{ num, dataUrl, w, h }]
  const [totalPages, setTotalPages] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setRendering(true);
        setErr(null);
        setPages([]);
        setTotalPages(0);
        const pdfjs = await loadPdfjs();
        // Copy the buffer because pdfjs consumes/detaches it internally.
        const data = arrayBuffer instanceof ArrayBuffer ? arrayBuffer.slice(0) : arrayBuffer;

        // Try worker first, then fall back to main-thread mode if the worker file
        // is blocked (CSP, network, iframe sandbox…). Main-thread is slower for
        // huge PDFs but bulletproof for small ones.
        let pdf;
        try {
          pdf = await pdfjs.getDocument({ data }).promise;
        } catch (workerErr) {
          if (cancelled) return;
          console.warn("[PdfViewer] worker failed, retrying in main thread:", workerErr?.message);
          const data2 = arrayBuffer instanceof ArrayBuffer ? arrayBuffer.slice(0) : arrayBuffer;
          pdf = await pdfjs.getDocument({ data: data2, disableWorker: true }).promise;
        }
        if (cancelled) { try { pdf.destroy(); } catch {} return; }
        setTotalPages(pdf.numPages);

        const targetWidth = Math.min(900, Math.max(320, window.innerWidth - 120));

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break;
          const page = await pdf.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const scale = targetWidth / base.width;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          const ctx = canvas.getContext("2d");
          await page.render({ canvasContext: ctx, viewport }).promise;
          if (cancelled) break;
          const dataUrl = canvas.toDataURL("image/png");
          // Free the canvas memory
          canvas.width = 0; canvas.height = 0;
          setPages((prev) => [...prev, { num: i, dataUrl, w: viewport.width, h: viewport.height }]);
        }
        if (!cancelled) setRendering(false);
        try { pdf.destroy(); } catch {}
      } catch (e) {
        if (cancelled) return;
        console.warn("[PdfViewer] render failed:", e);
        setErr(e?.message || "PDF failed to render");
        setRendering(false);
        onError?.(e);
      }
    })();

    return () => { cancelled = true; };
  }, [arrayBuffer, onError]);

  return (
    <div className="w-full h-full flex flex-col bg-[#F7F5F0]" data-testid="pdf-viewer">
      <div className="flex-1 overflow-auto p-4">
        {pages.map((p) => (
          <div key={p.num} className="mb-4 flex flex-col items-center gap-1" data-testid={`pdf-page-${p.num}`}>
            <img
              src={p.dataUrl}
              alt={`Page ${p.num}`}
              className="shadow-md max-w-full"
              style={{ width: p.w, height: p.h }}
            />
            <div className="text-[10px] text-[#8A847A] font-mono">Page {p.num}{totalPages ? ` / ${totalPages}` : ""}</div>
          </div>
        ))}
        {rendering && !err && (
          <div className="text-[#8A847A] flex items-center justify-center gap-2 py-8">
            <Loader2 className="h-5 w-5 animate-spin"/>
            {pages.length === 0 ? "Loading PDF…" : `Rendering page ${pages.length + 1}${totalPages ? ` of ${totalPages}` : ""}…`}
          </div>
        )}
        {err && (
          <div className="text-center p-8 text-[#8A3E30]" data-testid="pdf-error">
            PDF failed to render: {err}
          </div>
        )}
      </div>
      {!rendering && !err && totalPages > 0 && (
        <div className="text-center py-2 text-[11px] text-[#8A847A] border-t border-[#E5E0D8] bg-white">
          {totalPages} {totalPages === 1 ? "page" : "pages"} · scroll to view all
        </div>
      )}
    </div>
  );
}
